/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Paper,
  IconButton,
  Button,
  Badge,
  Breadcrumbs,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { GlossaryDialog } from '../components/glossary/GlossaryCollectionsDialog';
import { getStore, RootState } from '@ink-ai/portal/reducers';
import {
  translationActions,
  uploadAndTranslate,
} from '@ink-ai/portal/reducers/translation';
import { app } from '@ink-ai/portal/reducers/app';
import { TextTranslateArea } from '../components/translation/TextTranslateArea';
import { DocumentTranslationOptionDtoTargetLanguageEnum } from '@ink-ai/insight-service-sdk';
import { compose } from '@ink-ai/portal/reducers/compose';
import { Link, useBlocker } from 'react-router-dom';
import { ReferenceStoreDialog } from '../components/reference-store/ReferenceStoreDialog';
import { PaperCard } from '../components/Common';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';
import { BookOpenText } from 'lucide-react';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';

const confirmExit = () => {
  const translation = getStore().getState().translation;
  if (translation.translationStatus === 'uploading') {
    return 'The document is still in the process of uploading. Are you sure, you want to close?';
  }
};

export const Translation: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const store = getStore();
  const composeState = useSelector((state: RootState) => state.compose);
  const translation = useSelector((state: RootState) => state.translation);
  const [anchorElSource, setAnchorElSource] = useState<null | HTMLElement>(
    null,
  );
  const [anchorElTarget, setAnchorElTarget] = useState<null | HTMLElement>(
    null,
  );
  const [translationStyle, setTranslationStyle] = useState<string>('');
  const [isMergeEnabled, setIsMergeEnabled] = useState<boolean>(false);
  const [languageDirection, setLanguageDirection] = useState<string>('auto');
  const sourceText = useSelector((state: RootState) => state.translation.sourceText);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      translation.translationStatus === 'uploading' &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    window.onbeforeunload = confirmExit;
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  // 默认勾选 name 为 "By default" 的词库与参考资料文件夹（进入页面时）
  useEffect(() => {
    let cancelled = false;

    const extractExplorerCollections = (resp: any): any[] => {
      // 兼容新旧返回：优先 collections，其次 items
      const candidates = [
        resp?.collections,
        resp?.data?.collections,
        resp?.data?.data?.collections,
        resp?.items,
        resp?.data?.items,
        resp?.data?.data?.items,
      ];
      for (const list of candidates) {
        if (Array.isArray(list)) return list;
      }
      return [];
    };

    const isFolderEntry = (it: any): boolean => {
      const t = String(it?.itemType ?? it?.type ?? '').toLowerCase();
      return t === 'folder';
    };

    const dedupeStringIds = (ids: any[]): string[] => {
      return Array.from(new Set((ids || []).filter((x) => typeof x === 'string')));
    };

    const preselectDefaultFolders = async () => {
      try {
        // 预选 Glossary 文件夹
        const glossaryResp: any = await makeRequest({
          method: 'GET',
          url: '/api/v1/glossary/glossaryExplorer',
          params: { page: 1, pageSize: 50, name: 'By default', status: 'Ready' },
        });
        const glossaryItems = extractExplorerCollections(glossaryResp);
        const glossaryDefaultFolders: any[] = glossaryItems
          .filter((it: any) => isFolderEntry(it) && it?.name === 'By default' && typeof it.uuid === 'string')
        const glossaryFolderIds: string[] = glossaryDefaultFolders.map((it: any) => it.uuid);
        let glossaryChildIds: string[] = [];
        // 优先使用同一次响应中的 children（collections）
        for (const f of glossaryDefaultFolders) {
          const children = Array.isArray(f.collections) ? f.collections : [];
          const ids = children
            .filter((c: any) => typeof c?.uuid === 'string')
            .map((c: any) => c.uuid);
          glossaryChildIds.push(...ids);
        }
        // 若上述未获取到子项，则回退按文件夹逐个查询子文件
        if (glossaryChildIds.length === 0 && glossaryFolderIds.length > 0) {
          for (const folderId of glossaryFolderIds) {
            try {
              const childResp: any = await makeRequest({
                method: 'GET',
                url: '/api/v1/glossary/glossaryExplorer',
                params: { parentId: folderId, page: 1, pageSize: 200 },
              });
              const childItems = extractExplorerCollections(childResp);
              const ids = childItems
                .filter((it: any) => String(it?.itemType ?? it?.type ?? '').toLowerCase() !== 'folder' && typeof it?.uuid === 'string')
                .map((it: any) => it.uuid);
              glossaryChildIds.push(...ids);
            } catch (e) {
              console.warn('Glossary child fetch failed for folder:', folderId, e);
            }
          }
        }

        // 预选 Reference 文件夹
        const refResp: any = await makeRequest({
          method: 'GET',
          url: '/api/v1/referenceStore/referenceExplorer',
          params: { page: 1, pageSize: 50, name: 'By default', status: 'Ready' },
        });
        const refItems = extractExplorerCollections(refResp);
        const referenceDefaultFolders: any[] = refItems
          .filter((it: any) => isFolderEntry(it) && it?.name === 'By default' && typeof it.uuid === 'string')
        const referenceFolderIds: string[] = referenceDefaultFolders.map((it: any) => it.uuid);
        let referenceChildIds: string[] = [];
        for (const f of referenceDefaultFolders) {
          const children = Array.isArray(f.collections) ? f.collections : [];
          const ids = children
            .filter((c: any) => typeof c?.uuid === 'string')
            .map((c: any) => c.uuid);
          referenceChildIds.push(...ids);
        }
        if (referenceChildIds.length === 0 && referenceFolderIds.length > 0) {
          for (const folderId of referenceFolderIds) {
            try {
              const childResp: any = await makeRequest({
                method: 'GET',
                url: '/api/v1/referenceStore/referenceExplorer',
                params: { parentId: folderId, page: 1, pageSize: 200 },
              });
              const childItems = extractExplorerCollections(childResp);
              const ids = childItems
                .filter((it: any) => String(it?.itemType ?? it?.type ?? '').toLowerCase() !== 'folder' && typeof it?.uuid === 'string')
                .map((it: any) => it.uuid);
              referenceChildIds.push(...ids);
            } catch (e) {
              console.warn('Reference child fetch failed for folder:', folderId, e);
            }
          }
        }

        if (cancelled) return;

        // 如果存在匹配的文件夹，则默认勾选（包含重名的全部勾选）
        const dedupedGlossaryFolderIds = dedupeStringIds(glossaryFolderIds);
        const dedupedReferenceFolderIds = dedupeStringIds(referenceFolderIds);
        if (dedupedGlossaryFolderIds.length > 0) {
          dispatch(translationActions.setSelectedGlossariesFolders(dedupedGlossaryFolderIds));
        }
        if (dedupedReferenceFolderIds.length > 0) {
          dispatch(translationActions.setSelectedReferenceStoreFolders(dedupedReferenceFolderIds));
        }
        // 级联勾选子文件（仅限直接子项）
        const dedupedGlossaryChildIds = dedupeStringIds(glossaryChildIds);
        if (dedupedGlossaryChildIds.length > 0) {
          const union = Array.from(new Set([...(getStore().getState().translation.selectedGlossaries || []), ...dedupedGlossaryChildIds]));
          dispatch(translationActions.setSelectedGlossaries(union));
        }
        const dedupedReferenceChildIds = dedupeStringIds(referenceChildIds);
        if (dedupedReferenceChildIds.length > 0) {
          const union = Array.from(new Set([...(getStore().getState().translation.selectedReferenceStores || []), ...dedupedReferenceChildIds]));
          dispatch(translationActions.setSelectedReferenceStores(union));
        }
      } catch (e) {
        console.error('Failed to preselect "By default" folders:', e);
      }
    };

    preselectDefaultFolders();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(() => {
    // Clean up on unmount
    return () => {
      dispatch(translationActions.clearAll());
      dispatch(compose.actions.clearAll());
    };
  }, []);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    handleFileChange({
      target: { files },
    } as React.ChangeEvent<HTMLInputElement>);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/pdf',
      'application/json',
    ];

    if (file && allowedTypes.includes(file.type)) {
      dispatch(translationActions.setSelectedFile(file));
      dispatch(translationActions.setFileName(file.name));
      dispatch(translationActions.setFileSize(file.size));
    } else {
      dispatch(translationActions.setSelectedFile(null));
      store.dispatch(
        app.actions.setGlobalMessage({
          message: 'Invalid file type. Please upload a .docx, .pdf, or .json file.',
          status: 'error',
        }),
      );
    }
  };

  const handleSwapLanguages = () => {
    const newSource = translation.targetLanguage;
    const newTarget = translation.sourceLanguage;
    dispatch(translationActions.setSourceLanguage(newSource));
    dispatch(translationActions.setTargetLanguage(newTarget));
    if (translation.mode === 'Text' && composeState.generatedText) {
      dispatch(translationActions.setSourceText(composeState.generatedText));
      dispatch(compose.actions.clearAll());
    }
  };

  const handleGlossaryButtonClick = () => {
    dispatch(translationActions.setGlossaryDialogOpen(true));
  };

  const handleGlossaryDialogClose = () => {
    dispatch(translationActions.setGlossaryDialogOpen(false));
  };

  const handleGlossaryApply = (
    selectedGlossaryUuids: string[],
    selectedGlossaryFolderUuids: string[],
  ) => {
    const dedupedGlossaryUuids = Array.from(new Set((selectedGlossaryUuids || []).filter((x) => typeof x === 'string')));
    const dedupedGlossaryFolderUuids = Array.from(new Set((selectedGlossaryFolderUuids || []).filter((x) => typeof x === 'string')));
    dispatch(translationActions.setSelectedGlossaries(dedupedGlossaryUuids));
    dispatch(translationActions.setSelectedGlossariesFolders(dedupedGlossaryFolderUuids));
    dispatch(translationActions.setGlossaryDialogOpen(false));
  };

  const handleReferenceStoreButtonClick = () => {
    dispatch(translationActions.setReferenceStoreDialogOpen(true));
  };

  const handleReferenceStoreDialogClose = () => {
    dispatch(translationActions.setReferenceStoreDialogOpen(false));
  };

  const handleReferenceStoreApply = (
    selectedReferenceUuids: string[],
    selectedReferenceFolderUuids: string[],
  ) => {
    const dedupedReferenceUuids = Array.from(new Set((selectedReferenceUuids || []).filter((x) => typeof x === 'string')));
    const dedupedReferenceFolderUuids = Array.from(new Set((selectedReferenceFolderUuids || []).filter((x) => typeof x === 'string')));
    dispatch(translationActions.setSelectedReferenceStores(dedupedReferenceUuids));
    dispatch(translationActions.setSelectedReferenceStoreFolders(dedupedReferenceFolderUuids));
    dispatch(translationActions.setReferenceStoreDialogOpen(false));
  };

  const handleTranslate = async () => {
    try {
      dispatch(translationActions.setLoading(true));
      if (translation.selectedFile) {
        dispatch(uploadAndTranslate());
      } else {
        console.error('No file selected.');
      }
    } catch (error) {
      console.error(error);
      dispatch(translationActions.setLoading(false));
    }
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) return `${size} bytes`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} kb`;
    if (size < 1024 * 1024 * 1024)
      return `${(size / (1024 * 1024)).toFixed(2)} mb`;
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} gb`;
  };

  const handleSourceLanguageChange = (
    language: DocumentTranslationOptionDtoTargetLanguageEnum,
  ) => {
    if (language === translation.targetLanguage) {
      handleSwapLanguages();
    } else {
      dispatch(translationActions.setSourceLanguage(language));
    }
  };

  const handleTargetLanguageChange = (
    language: DocumentTranslationOptionDtoTargetLanguageEnum,
  ) => {
    if (language === translation.sourceLanguage) {
      handleSwapLanguages();
    } else {
      dispatch(translationActions.setTargetLanguage(language));
    }
  };

  useEffect(() => {
    // Sync language direction with Redux state, but respect 'auto' selection
    if (languageDirection !== 'auto') {
      if (
        translation.sourceLanguage ===
          DocumentTranslationOptionDtoTargetLanguageEnum.EnUs &&
        translation.targetLanguage ===
          DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn
      ) {
        setLanguageDirection('en-zh');
      } else if (
        translation.sourceLanguage ===
          DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn &&
        translation.targetLanguage ===
          DocumentTranslationOptionDtoTargetLanguageEnum.EnUs
      ) {
        setLanguageDirection('zh-en');
      }
    }
  }, [translation.sourceLanguage, translation.targetLanguage, languageDirection]);

  const handleLanguageDirectionChange = (direction: string) => {
    setLanguageDirection(direction);
    if (direction === 'en-zh') {
      dispatch(
        translationActions.setSourceLanguage(
          DocumentTranslationOptionDtoTargetLanguageEnum.EnUs,
        ),
      );
      dispatch(
        translationActions.setTargetLanguage(
          DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn,
        ),
      );
    } else if (direction === 'zh-en') {
      dispatch(
        translationActions.setSourceLanguage(
          DocumentTranslationOptionDtoTargetLanguageEnum.ZhCn,
        ),
      );
      dispatch(
        translationActions.setTargetLanguage(
          DocumentTranslationOptionDtoTargetLanguageEnum.EnUs,
        ),
      );
    } else if (direction === 'auto') {
      // Keep current Redux languages; backend will infer source language
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <Link to="/">{t('translation.navigation.home')}</Link>
        {/* <Link to="/translate-tasks">
          {t('translation.navigation.translateTasks')}
        </Link>
        <Typography color="textPrimary">
          {t('translation.navigation.new')}
        </Typography> */}
        <Typography color="textPrimary">
          {t('translation.navigation.textTranslate')}
        </Typography>
      </Breadcrumbs>
      <PaperCard>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          {/* Left: Language Direction Dropdown (left-aligned) */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200, mr: 2 }}>
              <InputLabel id="language-direction-label">
                {t('translation.languageDirection.label')}
              </InputLabel>
              <Select
                labelId="language-direction-label"
                id="language-direction-select"
                value={languageDirection}
                label={t('translation.languageDirection.label')}
                onChange={(e) => handleLanguageDirectionChange(e.target.value)}
                sx={{ '& .MuiSelect-select, & .MuiInputBase-input': { textAlign: 'left' } }}
              >
                <MenuItem value="auto">
                  {t('translation.languages.autoDetect')}
                </MenuItem>
                <MenuItem value="en-zh">
                  {t('translation.languages.enUs')} → {t('translation.languages.zhCn')}
                </MenuItem>
                <MenuItem value="zh-en">
                  {t('translation.languages.zhCn')} → {t('translation.languages.enUs')}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Right: Style, Merge, Glossary and Reference buttons (right-aligned) */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {/* Style Selection Dropdown */}
            {/* <FormControl variant="standard" size="small" sx={{ minWidth: 140, mr: 4, mb: 1 }}>
              <InputLabel id="translation-style-label">
                {t('translation.buttons.style')}
              </InputLabel>
              <Select
                labelId="translation-style-label"
                id="translation-style-select"
                value={translationStyle}
                label="Style"
                onChange={(e) => setTranslationStyle(e.target.value)}
                sx={{ '& .MuiSelect-select, & .MuiInputBase-input': { textAlign: 'left' } }}
              >
                <MenuItem value="Colloquial">
                  {t('translation.style.colloquial')}
                </MenuItem>
                <MenuItem value="Officialization">
                  {t('translation.style.officialization')}
                </MenuItem>
                <MenuItem value="and...">
                  {t('translation.style.and')}
                </MenuItem>
              </Select>
            </FormControl> */}
            
            {/* Merge Switch with tooltip */}
            <Tooltip title={t('translation.buttons.mergeTooltip')} placement="top">
              <FormControlLabel
                control={
                  <Switch
                    checked={isMergeEnabled}
                    onChange={(e) => setIsMergeEnabled(e.target.checked)}
                    name="mergeSwitch"
                    color="primary"
                  />
                }
                label={t('translation.buttons.merge')}
                sx={{ mr: 2 }}
              />
            </Tooltip>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Badge
                badgeContent={translation.selectedGlossaries.length}
                color="error"
              >
                <Button
                  onClick={handleGlossaryButtonClick}
                  variant="outlined"
                  disabled={translation.translationStatus === 'uploading'}
                  sx={{ ml: 2, textTransform: 'none', minWidth: 130, gap: '6px' }}
                >
                  <BookOpenText/>
                  {t('translation.buttons.glossary')}
                </Button>
              </Badge>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Badge
                badgeContent={translation.selectedReferenceStores.length}
                color="error"
              >
                <Button
                  onClick={handleReferenceStoreButtonClick}
                  variant="outlined"
                  disabled={translation.translationStatus === 'uploading'}
                  sx={{ ml: 2, textTransform: 'none', minWidth: 130, gap: '6px' }}
                >
                  <LibraryBooksOutlinedIcon/>
                  {t('translation.buttons.reference')}
                </Button>
              </Badge>
            </Box>
          </Box>
        </Box>
        {/* Removed per requirement: no separate source/target language selectors */}
        {/* Document translation lives on its own page; this page is text-only. */}
        <TextTranslateArea
          sourceLanguage={translation.sourceLanguage}
          targetLanguage={translation.targetLanguage}
          autoMerge={isMergeEnabled}
          languageDirection={languageDirection}
        />
      </PaperCard>

      <GlossaryDialog
        open={translation.glossaryDialogOpen}
        onClose={handleGlossaryDialogClose}
        onApply={handleGlossaryApply}
        initialSelectedGlossaries={translation.selectedGlossaries}
        initialSelectedFolders={translation.selectedGlossariesFolders}
        cascadeSelection
        resetSelectionOnFolderNav={false}
      />

      <ReferenceStoreDialog
        open={translation.referenceStoreDialogOpen}
        onClose={handleReferenceStoreDialogClose}
        onApply={handleReferenceStoreApply}
        initialSelectedReferenceStores={translation.selectedReferenceStores}
        initialSelectedFolders={translation.selectedReferenceStoreFolders}
        cascadeSelection
        resetSelectionOnFolderNav={false}
      />

      <Dialog open={blocker.state === 'blocked'}>
        <DialogTitle>{t('translation.dialog.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('translation.dialog.content')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.proceed()}>
            {t('translation.dialog.leave')}
          </Button>
          <Button
            onClick={() => blocker.reset()}
            color="primary"
            variant="contained"
            autoFocus
          >
            {t('translation.dialog.stay')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
